import { Spinner } from '@assembly-js/design-system';
import React, { useEffect, useState } from 'react';
import { Channel as ChannelType } from 'stream-chat';
import 'stream-chat-react/dist/css/v2/index.css';

import { InnerChannel } from 'src/features/channel/InnerChannel';
import { CustomMessageNotification } from 'src/features/messages/CustomMessageNotification';
import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';
import { useIsProxySession } from 'src/hooks/useIsProxySession';
import { useAppSelector } from 'src/hooks/useStore';
import { ChatChannelContent } from 'src/legacy/components/Chat/ChatChannelContent';
// eslint-disable-next-line no-restricted-imports
import { MessagesListEmptyState } from 'src/legacy/components/Chat/MessagesListEmptyState';
import { useChatClient } from 'src/legacy/components/Chat/useChatClient';
import { useMessagesStyles } from 'src/routes/messaging/MessagesPageStyles';

interface ChannelMentionProps {
  channelId: string;
  messageId?: string;
}

const ChannelMentionComponent = ({
  channelId,
  messageId,
}: ChannelMentionProps) => {
  const chatClient = useChatClient();
  const userId = useAppSelector((state) => state.user.id);
  const isProxySession = useIsProxySession(userId);
  const { isSidebarTheme } = useMessagingTheme();
  const { classes } = useMessagesStyles({
    showMessagingSidebar: false,
    isSidebarTheme,
  });

  const [channel, setChannel] = useState<ChannelType | undefined>(undefined);

  useEffect(() => {
    if (!chatClient || !channelId) return;

    let cancelled = false;
    const fetchChannel = async () => {
      const ch = chatClient.channel('messaging', channelId);
      await ch.watch();
      if (cancelled) return;
      if (ch.data?.member_count) {
        setChannel(ch);
      }
    };
    fetchChannel().catch((e) => {
      console.error(e);
    });
    return () => {
      cancelled = true;
    };
  }, [chatClient, channelId]);

  // Once the channel is loaded and a target message id is provided, jump the
  // chat to that message so the mention is visible without scrolling.
  useEffect(() => {
    if (channel && messageId) {
      void channel.state.loadMessageIntoState(messageId).catch(() => {
        // ignored — stream-chat-react will fall back to default state
      });
    }
  }, [channel, messageId]);

  if (!channel) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size={10} />
      </div>
    );
  }

  return (
    <div className={`${classes.root} w-full overflow-y-hidden`}>
      <InnerChannel
        channel={channel}
        EmptyStateIndicator={MessagesListEmptyState}
        MessageNotification={CustomMessageNotification}
        markReadOnMount={!isProxySession}
      >
        <ChatChannelContent showChannelToolbar={false} />
      </InnerChannel>
    </div>
  );
};

export const ChannelMention = React.memo(ChannelMentionComponent);
